<template>
    <v-container class="ma-0 pa-0">
        <v-row class="mb-5 v-sidebar__gallery" v-bind:class="{ 'only-one': galleryImages.length === 1 }">
            <v-col v-if="$lds.isEmpty(galleryImages)" class="">
                <v-skeleton-loader type="image" width="100%" height="360" style="margin-bottom: 4px"></v-skeleton-loader>
                <v-skeleton-loader v-if="galleryImages.length >= 2" type="image" width="100%" height="87"></v-skeleton-loader>
            </v-col>
            <v-col v-else>
                <VueSlickCarousel
                    ref="gCarousel1"
                    v-bind:asNavFor="gSlider2"
                    v-bind="gHerosOption"
                    class="overflow-hidden v-sidebar__gallery-heros"
                    v-on:afterChange="stopAutoplayAtFirstImage"
                >
                    <div v-for="(photo, i) in galleryImages" v-bind:key="`C-${i}`" class="c-pointer v-sidebar__gallery-image">
                        <img v-bind:src="photo" draggable="false" width="100%" alt="preview" class="img-fluid" />
                    </div>
                </VueSlickCarousel>
                <VueSlickCarousel
                    v-if="galleryImages.length > 1 || has360"
                    ref="gCarousel2"
                    v-bind:asNavFor="gSlider1"
                    v-bind="gThumbsOption"
                    class="overflow-hidden v-sidebar__gallery-thumbs"
                >
                    <div
                        v-for="(photo, i) in galleryImages"
                        v-bind:key="`C-${i}`"
                        class="c-pointer v-sidebar__gallery-image"
                        v-on:click="goToImage(i)"
                    >
                        <img v-bind:src="photo" draggable="false" width="100%" alt="preview thumbnail" class="img-fluid" />
                    </div>

                    <!-- 360° 뷰어 진입 썸네일 -->
                    <div
                        v-if="has360"
                        class="c-pointer v-sidebar__gallery-image v-gallery__360-thumb"
                        @click="open360Viewer"
                    >
                        <img
                            :src="photos360[0]"
                            draggable="false"
                            width="100%"
                            alt="360° 뷰어"
                            class="img-fluid"
                        />
                        <div class="v-gallery__360-badge">
                            <svg viewBox="0 0 36 36" width="22" height="22" fill="currentColor">
                                <path d="M18 4C10.3 4 4 10.3 4 18s6.3 14 14 14 14-6.3 14-14S25.7 4 18 4zm0 25.5c-6.3 0-11.5-5.2-11.5-11.5S11.7 6.5 18 6.5 29.5 11.7 29.5 18 24.3 29.5 18 29.5z"/>
                                <path d="M24 11l-10 5 10 5z"/>
                            </svg>
                            <span>360°</span>
                        </div>
                    </div>
                </VueSlickCarousel>
            </v-col>
        </v-row>

        <!-- 제품 프리뷰 버튼 -->
        <div class="mt-2 mb-5">
            <button class="preview-btn" type="button" @click.stop="openPreview">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                제품 미리보기
            </button>
        </div>

        <!-- 360° 뷰어 모달 -->
        <v-dialog v-model="show360Viewer" max-width="760" content-class="v-dialog--360">
            <Product360Viewer
                v-if="show360Viewer"
                :frames="photos360"
                @close="show360Viewer = false"
            />
        </v-dialog>
    </v-container>
</template>

<script>
    import VueSlickCarousel from 'vue-slick-carousel';
    import Product360Viewer from './Product360Viewer.vue';

    export default {
        name: 'ProductGallery',

        props: {
            productDetails: {
                type: Object,
                default: () => ({}),
            },
            galleryImages: {
                type: Array,
                required: true,
                default: () => [],
            },
        },

        components: {
            VueSlickCarousel,
            Product360Viewer,
        },

        watch: {
            productDetails: {
                immediate: true,
                handler(newVal) {},
            },
            galleryImages: {
                immediate: true,
                handler(newVal) {
                    if (!this.$lds.isEmpty(newVal)) {
                        this.galleryHasReachedEnd = false;
                        this.$nextTick(() => {
                            this.gSlider1 = this.$refs.gCarousel1;
                            this.gSlider2 = this.$refs.gCarousel2;
                        });
                    }
                },
            },
        },

        data: () => ({
            gSlider1: null,
            gSlider2: null,
            galleryHasReachedEnd: false,
            show360Viewer: false,
            gHerosOption: {
                lazyLoad: 'ondemand',
                infinite: true,
                autoplay: true,
                autoplaySpeed: 5000,
                speed: 500,
                slidesToShow: 1,
                slidesToScroll: 1,
                focusOnSelect: true,
                pauseOnHover: true,
                arrows: true,
            },
            gThumbsOption: {
                lazyLoad: 'ondemand',
                infinite: true,
                speed: 500,
                slidesToShow: 4,
                slidesToScroll: 1,
                swipeToSlide: true,
                arrows: false,
            },
        }),

        computed: {
            photos360() {
                return (this.productDetails && this.productDetails.photos_360) || [];
            },
            has360() {
                return this.photos360.length > 0;
            },
        },

        methods: {
            openPreview() {
                const url = '/product-preview';
                const w = Math.min(window.screen.width - 100, 1200);
                const h = Math.min(window.screen.height - 80, 750);
                const left = Math.round((window.screen.width - w) / 2);
                const top  = Math.round((window.screen.height - h) / 2);
                window.open(
                    url,
                    'product-preview',
                    `width=${w},height=${h},left=${left},top=${top},resizable=yes,scrollbars=no`
                );
            },

            open360Viewer() {
                this.show360Viewer = true;
            },

            goToImage(index) {
                if (this.gSlider1) this.gSlider1.goTo(index, true);
            },

            // 자동재생이 마지막 이미지까지 갔다가 다시 첫 이미지로 돌아오면(한 바퀴 완료) 멈춘다.
            stopAutoplayAtFirstImage(currentSlide) {
                if (!this.gSlider1) return;

                const lastIndex = this.galleryImages.length - 1;
                if (currentSlide >= lastIndex) {
                    this.galleryHasReachedEnd = true;
                } else if (currentSlide === 0 && this.galleryHasReachedEnd) {
                    this.gSlider1.pause();
                }
            },
        },
    };
</script>

<style lang="scss" scoped>
    // 기본 테마는 화살표를 캐러셀 바깥(음수 left/right)에 배치하는데, 부모에 overflow-hidden 이 있어
    // 화면 밖으로 잘려 보이지 않는다. 이미지 안쪽으로 들어오게 위치를 옮기고, 사진 위에서도 잘 보이도록
    // 어두운 원형 배경을 추가한다.
    .preview-btn {
        width: 100%;
        padding: 10px 0;
        border: 1.5px solid #19253d;
        background: #fff;
        color: #19253d;
        font-size: 14px;
        font-weight: 500;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 7px;
        cursor: pointer;
        border-radius: 4px;
        transition: background 0.15s, color 0.15s, transform 0.1s;
        letter-spacing: 0.01em;
    }
    .preview-btn:hover {
        background: #19253d;
        color: #fff;
        transform: translateY(-1px);
    }
    .preview-btn:active {
        transform: translateY(0);
    }

    :deep(.v-sidebar__gallery-heros) {
        .slick-prev,
        .slick-next {
            z-index: 2;
            width: 36px;
            height: 36px;
            background: rgba(0, 0, 0, 0.35);
            border-radius: 50%;
        }
        .slick-prev {
            left: 10px;
        }
        .slick-next {
            right: 10px;
        }
        .slick-prev:before,
        .slick-next:before {
            font-size: 18px;
            color: #fff;
            opacity: 1;
        }
    }

    // 360° 썸네일
    .v-gallery__360-thumb {
        position: relative;
        overflow: hidden;

        img {
            filter: brightness(0.7);
        }
    }

    .v-gallery__360-badge {
        position: absolute;
        inset: 0;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        color: #fff;
        font-size: 11px;
        font-weight: 700;
        letter-spacing: 0.04em;
        gap: 2px;
        background: rgba(25, 37, 61, 0.45);
        transition: background 0.15s;

        span {
            margin-top: 2px;
        }
    }

    .v-gallery__360-thumb:hover .v-gallery__360-badge {
        background: rgba(25, 37, 61, 0.7);
    }
</style>
